import { Permission } from '@server/permissions';
import { useSelector } from 'react-redux';
import { selectOrgPermissions, selectAccountPermissions } from '../../../reducers/userInfoReducer';
import { useCallback } from 'react';

export const usePermissions = () => {
  const orgPermissions = useSelector(selectOrgPermissions);
  const accountPermissions = useSelector(selectAccountPermissions);

  /**
   * Checks each permission in the array.
   * Account permissions have priority over org permissions.
   * If crmOrgId is not provided, only account permissions are checked.
   * @returns An array of booleans, where each boolean indicates if the corresponding permission is granted.
   */
  const checkPermissions = useCallback(
    (permissionArray: Permission[], crmOrgId?: string) => {
      return permissionArray.map((permission) => {
        // Account permissions have priority
        if (accountPermissions?.includes(permission)) {
          return true;
        }
        // If no crmOrgId provided, only check account permissions
        if (!crmOrgId) {
          return false;
        }
        // Check org-specific permissions
        const _orgPermissions = orgPermissions?.[crmOrgId];
        return _orgPermissions?.includes(permission) ?? false;
      });
    },
    [orgPermissions, accountPermissions],
  );

  /**
   * Checks if all permissions in the array are granted.
   * Account permissions have priority over org permissions.
   * If crmOrgId is not provided, only account permissions are checked.
   * @returns `true` if all permissions are granted, `false` otherwise.
   */
  const hasAllPermissions = useCallback(
    (permissionArray: Permission[], crmOrgId?: string) => {
      return checkPermissions(permissionArray, crmOrgId).every((permission) => permission);
    },
    [checkPermissions],
  );

  /**
   * Checks if at least one permission in the array is granted.
   * Account permissions have priority over org permissions.
   * If crmOrgId is not provided, only account permissions are checked.
   * @returns `true` if at least one permission is granted, `false` otherwise.
   */
  const hasAnyPermissions = useCallback(
    (permissionArray: Permission[], crmOrgId?: string) => {
      return checkPermissions(permissionArray, crmOrgId).some((permission) => permission);
    },
    [checkPermissions],
  );

  return {
    checkPermissions,
    hasAllPermissions,
    hasAnyPermissions,
  };
};
